import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login } from '@store/slices/authSlice';
import { setActiveModule } from '@store/slices/uiSlice';
import { Building2, Eye, EyeOff, LogIn, HardHat, Wallet } from 'lucide-react';

// Mock kullanıcı veritabanı
const MOCK_USERS = [
    {
        username: 'admin',
        password: 'admin',
        user: { id: '1', name: 'Müteahhit Admin', email: 'admin@insaet.com' },
        role: 'admin',
    },
    {
        username: 'sef',
        password: 'sef',
        user: { id: '2', name: 'Şantiye Şefi', email: 'sef@insaet.com' },
        role: 'sef',
    },
];

export default function LoginPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        username: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Mock login
        setTimeout(() => {
            const foundUser = MOCK_USERS.find(
                u => u.username === formData.username && u.password === formData.password
            );

            if (foundUser) {
                dispatch(login({
                    user: foundUser.user,
                    token: `mock-token-${foundUser.role}-${Date.now()}`,
                    role: foundUser.role,
                }));

                // Şef kullanıcısı sadece inşaat modülünü görebilir
                if (foundUser.role === 'sef') {
                    dispatch(setActiveModule('insaat'));
                    navigate('/insaat/ozet');
                } else {
                    dispatch(setActiveModule('finans'));
                    navigate('/finans/ozet');
                }
            } else {
                setError('Kullanıcı adı veya şifre hatalı!');
            }
            setLoading(false);
        }, 500);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleQuickLogin = (username, password) => {
        setFormData({ username, password });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary via-primary-dark to-neutral-900 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm mb-4">
                        <Building2 size={32} className="text-secondary" />
                    </div>
                    <h1 className="text-3xl font-bold text-white">İNŞA ET</h1>
                    <p className="text-neutral-300 mt-2">İnşaat Yönetim Sistemi</p>
                </div>

                {/* Login Card */}
                <div className="bg-bg-secondary rounded-2xl shadow-2xl p-8">
                    <h2 className="text-2xl font-bold text-text-primary mb-6 text-center">
                        Giriş Yap
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Username */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Kullanıcı Adı
                            </label>
                            <input
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                className="w-full px-4 py-3 border border-border-light rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all bg-bg-secondary text-text-primary"
                                placeholder="Kullanıcı adınızı girin"
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Şifre
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-border-light rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all pr-12 bg-bg-secondary text-text-primary"
                                    placeholder="Şifrenizi girin"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        {/* Error */}
                        {error && (
                            <div className="bg-error-50 text-error text-sm px-4 py-3 rounded-lg">
                                {error}
                            </div>
                        )}

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <>
                                    <LogIn size={20} />
                                    <span>Giriş Yap</span>
                                </>
                            )}
                        </button>
                    </form>

                    {/* Quick Login Buttons */}
                    <div className="mt-6 space-y-3">
                        <p className="text-xs text-text-tertiary text-center mb-3">
                            Hızlı Giriş (Demo)
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => handleQuickLogin('admin', 'admin')}
                                className="flex items-center justify-center gap-2 px-4 py-3 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors text-sm font-medium"
                            >
                                <Wallet size={18} />
                                <span>Müteahhit</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleQuickLogin('sef', 'sef')}
                                className="flex items-center justify-center gap-2 px-4 py-3 bg-secondary/10 hover:bg-secondary/20 text-secondary rounded-lg transition-colors text-sm font-medium"
                            >
                                <HardHat size={18} />
                                <span>Şantiye Şefi</span>
                            </button>
                        </div>
                    </div>

                    {/* Demo Info */}
                    <div className="mt-4 p-4 bg-bg-tertiary rounded-lg">
                        <div className="text-xs text-text-tertiary space-y-1">
                            <p><strong>Müteahhit:</strong> <code className="bg-neutral-200 dark:bg-neutral-700 px-1 rounded">admin / admin</code> (Tam erişim)</p>
                            <p><strong>Şantiye Şefi:</strong> <code className="bg-neutral-200 dark:bg-neutral-700 px-1 rounded">sef / sef</code> (Sadece İnşaat modülü)</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
